import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';

export type AidaTooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
    selector: '[aidaTooltip]',
    standalone: true,
})
export class AidaTooltipDirective implements OnChanges, OnDestroy {
    /** The tooltip text. Passing empty string or null suppresses the tooltip. */
    @Input('aidaTooltip') text = '';

    /** Position relative to the host element. Defaults to 'right'. */
    @Input() aidaTooltipPosition: AidaTooltipPosition = 'right';

    /** Gap in pixels between the host element edge and the tooltip. */
    @Input() aidaTooltipOffset = 8;

    /**
     * When true the tooltip will not be shown. Use for conditional suppression
     * (e.g. `[aidaTooltipDisabled]="!!openMenuId"`).
     */
    @Input() aidaTooltipDisabled = false;

    private tooltipEl: HTMLElement | null = null;

    /**
     * Hide any currently displayed tooltip immediately when suppression inputs change.
     * This prevents stale tooltips from remaining visible while menus/modals open.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['aidaTooltipDisabled'] || changes['text']) {
            if (this.aidaTooltipDisabled || !this.text) {
                this.removeTooltip();
            }
        }
    }

    /**
     * @param {ElementRef<HTMLElement>} el Host element reference used to position the tooltip.
     */
    constructor(private readonly el: ElementRef<HTMLElement>) {}

    /**
     *
     */
    @HostListener('mouseenter')
    show(): void {
        if (this.aidaTooltipDisabled || !this.text) {
            return;
        }
        this.createOrUpdateTooltip();
        this.position();
    }

    /**
     * link that opens a new tab, the browser re-fires `focus` on it when the user returns to this page.
     * the tooltip would stay on screen indefinitely.
     */
    @HostListener('focus')
    showOnKeyboardFocus(): void {
        if (!this.isFocusVisible()) {
            return;
        }
        this.show();
    }

    /**
     *
     */
    @HostListener('mouseleave')
    @HostListener('blur')
    @HostListener('click')
    hide(): void {
        this.removeTooltip();
    }

    /**
     * @returns {boolean} Whether the host currently matches `:focus-visible`.
     */
    private isFocusVisible(): boolean {
        try {
            return this.el.nativeElement.matches(':focus-visible');
        } catch {
            return true;
        }
    }

    /**
     *
     */
    ngOnDestroy(): void {
        this.removeTooltip();
    }

    /**
     *
     */
    private createOrUpdateTooltip(): void {
        if (!this.tooltipEl) {
            this.tooltipEl = document.createElement('div');
            this.tooltipEl.className = 'aida-tooltip';
            this.tooltipEl.style.position = 'fixed';
            this.tooltipEl.style.pointerEvents = 'none';
            document.body.appendChild(this.tooltipEl);
        }
        this.tooltipEl.textContent = this.text;
    }

    /**
     *
     */
    private removeTooltip(): void {
        if (this.tooltipEl) {
            this.tooltipEl.remove();
            this.tooltipEl = null;
        }
    }

    /**
     *
     */
    private position(): void {
        requestAnimationFrame(() => {
            if (!this.tooltipEl) {
                return;
            }
            const anchor = this.el.nativeElement.getBoundingClientRect();
            const tip = this.tooltipEl.getBoundingClientRect();
            const offset = this.aidaTooltipOffset;
            const margin = 8;

            let top: number;
            let left: number;

            switch (this.aidaTooltipPosition) {
                case 'bottom':
                    top = anchor.bottom + offset;
                    left = anchor.left + anchor.width / 2 - tip.width / 2;
                    break;
                case 'top':
                    top = anchor.top - offset - tip.height;
                    left = anchor.left + anchor.width / 2 - tip.width / 2;
                    break;
                case 'left':
                    top = anchor.top + anchor.height / 2 - tip.height / 2;
                    left = anchor.left - offset - tip.width;
                    break;
                case 'right':
                default:
                    top = anchor.top + anchor.height / 2 - tip.height / 2;
                    left = anchor.right + offset;
                    // flip to left if overflows right edge
                    if (left + tip.width > window.innerWidth - margin) {
                        left = anchor.left - offset - tip.width;
                    }
                    break;
            }

            // Clamp within viewport
            if (left < margin) {
                left = margin;
            }
            if (left + tip.width > window.innerWidth - margin) {
                left = window.innerWidth - tip.width - margin;
            }
            if (top < margin) {
                top = margin;
            }
            if (top + tip.height > window.innerHeight - margin) {
                top = Math.max(margin, window.innerHeight - tip.height - margin);
            }

            this.tooltipEl.style.top = `${Math.round(top)}px`;
            this.tooltipEl.style.left = `${Math.round(left)}px`;
            this.tooltipEl.style.zIndex = '200000';
        });
    }
}
